import React, { useState, useEffect } from "react";
import StockTransactionsPopup from "./ClientStockTransactionsPopup";
import "./css/Portfolio.css"; // Ensure correct styling
import {useSharingContext} from "../../contexts/SharingContext"; // ✅ Correct import
import {getLastClosePrice, getLastPrice} from "@portfolio/utils/PFService";
import {usePopupStack, useStackedPopup} from "../../contexts/PopupStackContext";

const StockPopup = ({ selectedStockTransactionsAcrossClients, setShowClientPopup, aggregatedSortedStocksPerClient, symbol }) => {
    const [showTransactionsPopup, setShowTransactionsPopup] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientTransactions, setClientTransactions] = useState([]);
    const [updatedStockTransactionsAcrossClients, setUpdatedStockTransactionsAcrossClients] = useState(null); // introduced so that when a refresh is required, it can be done using UseEffects
    const [selectedRow, setSelectedRow] = useState(null); // Track clicked row
    const {ticks} = useSharingContext();  // Get live tick data
    const {pfQuotes} = useSharingContext();
    /*const { push, pop } = usePopupStack();*/

    useEffect(() => {
        if (selectedStockTransactionsAcrossClients) {
            // 🔥 Compute derivedData whenever selectedStockTransactionsAcrossClients changes in parent Portfolio.js. this is so that updated values come into this component as well
            setUpdatedStockTransactionsAcrossClients(selectedStockTransactionsAcrossClients);
        }
    }, [selectedStockTransactionsAcrossClients]); // 🔥 Trigger recalculation when selectedItem updates

/*    useEffect(() => {
        const popupEntry = { onClose: () => setShowClientPopup(false) }; // replace `setShowPopup`
        push(popupEntry);
        return () => pop();
    }, []);*/

    useStackedPopup(true, () => {
        setShowClientPopup(false);
    });

    const handleClose = () => {
        setShowClientPopup(false);
    };

    if (!updatedStockTransactionsAcrossClients || updatedStockTransactionsAcrossClients.length === 0) return null;

    const handleRowClick = (uniqueRowId) => {
        setSelectedRow(uniqueRowId);
    };
    const handleClientRowClick = (clientId) => {
        setSelectedClient(clientId);
        // Extract only transactions for this client
        /*const transactionsForClient = updatedStockTransactionsAcrossClients.filter(stock => stock.clientId === clientId);
        setClientTransactions(transactionsForClient);*/
        setShowTransactionsPopup(true);
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                {/*<h3>{symbol ||
                    Object.values(aggregatedSortedStocksPerClient)
                        .flatMap(stocks => stocks) // Flatten stocks array
                        .find(stock => stock.symbol)?.symbol
                    || "Unknown Stock"}</h3>*/}
                <h4>{symbol}</h4>
                <div className={"stock-popup-table"}>
                <table border="1" cellPadding="5" cellSpacing="0"
                       style={{width: "100%", borderCollapse: "collapse"}}>
                    <thead>
                    <tr>
                        <th>Client ID</th>
                        <th style={{textAlign: "right"}}>Quantity <br/><span style={{fontSize: "small"}}>Avg Price</span>
                        </th>
                        <th style={{textAlign: "right"}}>Amount Invested</th>
                        <th style={{textAlign: "right"}}>Current Price</th>
                        <th style={{textAlign: "right"}}>Today's Gain <br/><span style={{fontSize: "small"}}>Gain%</span>
                        </th>
                        <th style={{textAlign: "right"}}>Overall Gain <br/><span style={{fontSize: "small"}}>Gain%</span>
                        </th>
                        <th style={{textAlign: "right"}}>Current Value</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.entries(aggregatedSortedStocksPerClient)
                        .filter(([clientId]) => clientId !== "Group") // ✅ Exclude "Group"
                        .map(([clientId, aggregatedStocksForClient]) => (
                            <React.Fragment key={clientId}>
                                {aggregatedStocksForClient
                                    .filter(stock => stock.symbol === symbol) // ✅ Filter stocks
                                    .map((stock, index) => {
                                        const uniqueRowId = `${clientId}-${index}`;
                                        // ✅ Extract tick using instrumentToken
                                        const tick = ticks?.[stock.instrumentEntity.instrumentToken] ?? null;

                                        // ✅ Compute dynamic values
                                        const lastPrice = tick?.lastTradedPrice ?? getLastPrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        const closePrice = tick?.closePrice ?? getLastClosePrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        const weightedAvgPrice = stock.totalBuyPrice / (stock.netQuantity || 1); // Avoid division by zero
                                        const currentValue = stock.netQuantity * lastPrice;
                                        const todayGain = (lastPrice - closePrice) * stock.netQuantity;
                                        const overallGain = currentValue - stock.amountInvested;
                                        const todayGainPercentage = (todayGain / (stock.amountInvested || 1)) * 100 || 0;
                                        const overallGainPercentage = (overallGain / (stock.amountInvested || 1)) * 100 || 0;

                                        return (
                                            <tr
                                                key={`${clientId}-${index}`}  // ✅ Ensures unique keys
                                                onDoubleClick={() => handleClientRowClick(clientId)}
                                                onClick={() => handleRowClick(uniqueRowId)}
                                                className={selectedRow === uniqueRowId ? "selected-row" : ""}
                                                /*style={{
                                                    backgroundColor: selectedRow === uniqueRowId ? "#edecec" : "transparent",
                                                    cursor: "pointer"
                                                }}*/
                                            >
                                                <td>{clientId || "N/A"}</td>
                                                <td style={{textAlign: "right"}}>
                                                    {stock.netQuantity || 0} <br/>
                                                    <span
                                                        style={{fontSize: "small"}}>{weightedAvgPrice.toFixed(2)}</span>
                                                </td>
                                                <td style={{textAlign: "right"}}>{(stock.amountInvested || 0).toFixed(2)}</td>
                                                <td style={{textAlign: "right"}}>{lastPrice.toFixed(2)}</td>
                                                {/* ✅ Use computed lastPrice */}
                                                <td style={{textAlign: "right"}}>
                                                    {todayGain.toFixed(2)} <br/>
                                                    <span
                                                        style={{fontSize: "small"}}>{todayGainPercentage.toFixed(2)}%</span>
                                                </td>
                                                <td style={{textAlign: "right"}}>
                                                    {overallGain.toFixed(2)} <br/>
                                                    <span
                                                        style={{fontSize: "small"}}>{overallGainPercentage.toFixed(2)}%</span>
                                                </td>
                                                <td style={{textAlign: "right"}}>{currentValue.toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                            </React.Fragment>
                        ))}
                    </tbody>

                    <tfoot>
                    <tr>
                        <td><strong>Total</strong></td>
                        <td style={{textAlign: "right"}}>
                            <strong>
                                {aggregatedSortedStocksPerClient["Group"]?.filter(stock => stock.symbol === symbol)
                                    .reduce((sum, stock) => sum + (stock.netQuantity || 0), 0).toFixed(2)}
                            </strong>
                        </td>
                        <td style={{textAlign: "right"}}>
                            <strong>
                                {aggregatedSortedStocksPerClient["Group"]?.filter(stock => stock.symbol === symbol)
                                    .reduce((sum, stock) => sum + (stock.amountInvested || 0), 0).toFixed(2)}
                            </strong>
                        </td>
                        <td></td>
                        <td style={{textAlign: "right"}}>
                            <strong>
                                {aggregatedSortedStocksPerClient["Group"]?.filter(stock => stock.symbol === symbol)
                                    .reduce((sum, stock) => {
                                        const tick = ticks?.[stock.instrumentEntity.instrumentToken] ?? null;
                                        const lastPrice = tick?.lastTradedPrice ?? getLastPrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        const closePrice = tick?.lastTradedPrice ?? getLastClosePrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        return sum + ((lastPrice - closePrice) * stock.netQuantity);
                                    }, 0).toFixed(2)}
                            </strong>
                        </td>
                        <td style={{textAlign: "right"}}>
                            <strong>
                                {aggregatedSortedStocksPerClient["Group"]?.filter(stock => stock.symbol === symbol)
                                    .reduce((sum, stock) => {
                                        const tick = ticks?.[stock.instrumentEntity.instrumentToken] ?? null;
                                        const lastPrice = tick?.lastTradedPrice ?? getLastPrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        const currentValue = stock.netQuantity * lastPrice;
                                        return sum + (currentValue - stock.amountInvested);
                                    }, 0).toFixed(2)}
                            </strong>
                        </td>
                        <td style={{textAlign: "right"}}>
                            <strong>
                                {aggregatedSortedStocksPerClient["Group"]?.filter(stock => stock.symbol === symbol)
                                    .reduce((sum, stock) => {
                                        const tick = ticks?.[stock.instrumentEntity.instrumentToken] ?? null;
                                        const lastPrice = tick?.lastTradedPrice ?? getLastPrice(stock.instrumentEntity.instrumentToken, pfQuotes);
                                        return sum + (stock.netQuantity * lastPrice);
                                    }, 0).toFixed(2)}
                            </strong>
                        </td>
                    </tr>
                    </tfoot>
                </table>
                </div>
                <div className="contract-buttons">
                    <button type="button" className="cancel-button" onClick={handleClose}>Close
                    </button>
                </div>
            </div>
            {showTransactionsPopup && selectedClient && (
                <StockTransactionsPopup
                    /*symbol={Object.values(aggregatedSortedStocksPerClient)
                        .flatMap(stocks => stocks) // Flatten stocks array
                        .find(stock => stock.symbol)?.symbol}*/
                        symbol={symbol}
                        clientId={selectedClient}
                        setShowTransactionsPopup={setShowTransactionsPopup}
                        /*currentPricePFStocks={currentPricePFStocks}*/
                    />
                )}
            </div>
            );
            };

            export default StockPopup;
